<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import AgentLayout from "./layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import deleteCampaign from "@/components/campaigns/deleteCampaign.vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useCampaignsStore } from "@/stores/campaigns";

const uiStore = useUiStore();
const campaignsStore = useCampaignsStore();
const router = useRouter();
const companyId = useCompanyStore().companyId;
let getModalState = computed(() => uiStore.modalState);

const isLoading = ref(false);
// Mock Data
const campaigns = ref([]);

// State
const searchQuery = ref("");
const filterStatus = ref("All");
const viewMode = ref("grid"); // 'grid' | 'list'
const statusOptions = ["All", "Active", "Draft"];

const action = async (action, data) => {
  //  uiStore.setModalAction(action);
  uiStore.setModalState(true);
  uiStore.setModalData(data);

  if (action == "edit_campaign") {
    router.push({ name: "editCampaign", params: { id: data.id } });
  }
};

const duplicatingId = ref(null);

const duplicateCampaign = async (camp) => {
  if (duplicatingId.value) return;
  duplicatingId.value = camp.id;
  try {
    const res = await campaignsStore.fetchCampaignById({ companyId, id: camp.id });
    const source = res.data;
    const payload = {
      name: `Copy of ${source.name}`,
      status: "draft",
      format: source.format,
      setting: source.setting,
      items: (source.items || []).map((item) => ({
        id: item.id,
        name: item.name,
        duration: item.duration,
        playUntilEnd: item.playUntilEnd,
      })),
      targeting: {
        stores: (source.targeting?.stores || []).map((s) => s.id),
        areas: (source.targeting?.areas || []).map((a) => a.id),
        devices: (source.targeting?.devices || []).map((d) => d.id),
      },
      schedules: (source.schedules || []).map((s) => ({
        startDate: s.startDate,
        endDate: s.endDate,
        startTime: s.startTime,
        endTime: s.endTime,
        type: s.type,
        recurrence: {
          daysOfWeek: s.recurrence?.daysOfWeek || [],
          daysOfMonth: s.recurrence?.daysOfMonth || [],
          monthsOfYear: s.recurrence?.monthsOfYear || [],
        },
      })),
    };
    await campaignsStore.addCampaign({ companyId, data: payload });
    fetchAllCampaignsGrid();
  } catch {
    // errors are already toasted by the store
  } finally {
    duplicatingId.value = null;
  }
};

// Computeds
const filteredCampaigns = computed(() => {
  return campaigns.value.filter((camp) => {
    const matchesSearch = camp.name
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase());
    const matchesStatus =
      filterStatus.value === "All" || camp.status === filterStatus.value;
    return matchesSearch && matchesStatus;
  });
});

// Methods
const navigateToCreateParams = () => {
  router.push({ name: "createCampaign" });
};

const toggleViewMode = (mode) => {
  viewMode.value = mode;
};

const refreshGrid = () => {
  document.querySelector(".modal-backdrop").remove();
  document.querySelector(".dx-device-desktop").classList.remove("modal-open");
  fetchAllCampaignsGrid();
};

const fetchAllCampaignsGrid = () => {
  isLoading.value = true;
  campaignsStore
    .fetchAllCampaignsGrid(companyId)
    .then((res) => {
      campaigns.value = res.data;
      isLoading.value = false;
    })
    .catch(() => {
    });
};

const formatLabel = (f) =>
  ({
    screensaver: "Full Screen",
    topbar: "Top Bar",
    slider_basic: "Slider Basic",
  })[f] || f || "—";

const formatIcon = (f) =>
  ({
    screensaver: "bi bi-aspect-ratio",
    topbar: "bi bi-view-list",
    slider_basic: "bi bi-sliders",
  })[f] || "bi bi-collection-play";

const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "status-active";
    case "Scheduled":
      return "status-scheduled";
    case "Draft":
      return "status-draft";
    case "Cancelled":
      return "status-cancelled";
    default:
      return "";
  }
};

onMounted(() => {
  fetchAllCampaignsGrid();
});
</script>

<template>
  <AgentLayout>
    <deleteCampaign v-if="getModalState" @refresh="refreshGrid" />
    <div class="library-container">
      <!-- Header -->
      <PageHeader
        title="Campaigns"
        subtitle="Manage your screen content and campaigns"
        :showButton="true"
        buttonText="New Campaign"
        @action-click="navigateToCreateParams"
      />

      <div class="card">
        <!-- Toolbar -->
        <div class="toolbar">
          <div class="left-controls">
            <div class="search-box">
              <i class="bi bi-search"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search Campaigns..."
              />
            </div>
            <div class="status-pills" role="group" aria-label="Filter by status">
              <button
                v-for="option in statusOptions"
                :key="option"
                type="button"
                class="status-pill"
                :class="{ active: filterStatus === option }"
                @click="filterStatus = option"
              >
                {{ option }}
              </button>
            </div>
          </div>

          <div class="view-controls">
            <button
              class="view-btn"
              :class="{ active: viewMode === 'list' }"
              @click="toggleViewMode('list')"
              title="List View"
            >
              <i class="bi bi-list-ul"></i>
            </button>
            <button
              class="view-btn"
              :class="{ active: viewMode === 'grid' }"
              @click="toggleViewMode('grid')"
              title="Grid View"
            >
              <i class="bi bi-grid-fill"></i>
            </button>
          </div>
        </div>

        <!-- Content Area -->
        <div class="content-area">
          <!-- Loading State -->
          <div v-if="isLoading" class="grid-view skeleton-grid">
            <div v-for="n in 8" :key="n" class="skeleton-card">
              <div class="skeleton-thumb"></div>
              <div class="skeleton-body">
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
              </div>
            </div>
          </div>

          <template v-else-if="filteredCampaigns.length > 0">
            <!-- Grid View -->
            <div v-if="viewMode === 'grid'" class="grid-view">
              <div
                v-for="(camp, idx) in filteredCampaigns"
                :key="camp.id"
                class="ad-card"
                :style="{ '--d': idx }"
              >
                <div
                  class="card-preview"
                  :class="{ 'has-thumbnail': !!camp.thumbnail }"
                >
                  <template v-if="camp.thumbnail">
                    <video
                      v-if="camp.type == 'video'"
                      :src="camp.thumbnail"
                      class="preview-media"
                      muted
                      loop
                      onmouseover="this.play()"
                      onmouseout="this.pause()"
                    ></video>
                    <img
                      v-else
                      :src="camp.thumbnail"
                      class="preview-media"
                      alt="Thumbnail"
                    />
                  </template>

                  <div v-if="!camp.thumbnail" class="preview-placeholder">
                    <i :class="formatIcon(camp.format)"></i>
                  </div>

                  <span class="format-chip">
                    <i :class="formatIcon(camp.format)"></i>
                    {{ formatLabel(camp.format) }}
                  </span>

                  <div
                    v-if="camp.thumbnail && camp.type == 'video'"
                    class="play-affordance"
                  >
                    <i class="bi bi-play-fill"></i>
                  </div>

                  <span class="duration-badge"
                    >{{ camp.itemCount }} <i class="bi bi-collection"></i
                  ></span>
                </div>
                <div class="card-header">
                  <h3>{{ camp.name }}</h3>
                  <span
                    class="status-badge"
                    :class="getStatusColor(camp.status)"
                    >{{ camp.status }}</span
                  >
                </div>
                <div class="card-details">
                  <p class="format-text">
                    {{ formatLabel(camp.format) }} • {{ camp.totalDuration }}s
                  </p>
                  <div class="card-meta">
                    <span>Last Modified: {{ camp.lastModified }}</span>
                  </div>
                </div>
                <div class="card-actions">
                  <button
                    @click="action('edit_campaign', camp)"
                    class="action-btn"
                    title="Edit"
                  >
                    <i class="bi bi-pencil-fill"></i>
                  </button>
                  <button
                    @click="duplicateCampaign(camp)"
                    class="action-btn"
                    title="Duplicate"
                    :disabled="duplicatingId === camp.id"
                  >
                    <i
                      :class="duplicatingId === camp.id ? 'bi bi-hourglass-split' : 'bi bi-copy'"
                    ></i>
                  </button>
                  <button
                    @click="action('delete_campaign', camp)"
                    class="action-btn delete"
                    title="Delete"
                  >
                    <i class="bi bi-trash-fill"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- List View -->
            <div v-else class="list-view">
              <div class="list-header">
                <span class="col-name">Name</span>
                <span class="col-format">Format</span>
                <span class="col-status">Status</span>
                <span class="col-duration">Items / Duration</span>
                <span class="col-date">Last Modified</span>
                <span class="col-actions">Actions</span>
              </div>
              <div
                v-for="camp in filteredCampaigns"
                :key="camp.id"
                class="list-row"
              >
                <div class="col-name">
                  <div class="row-icon">
                    <template v-if="camp.thumbnail">
                      <video
                        v-if="camp.type == 'video'"
                        :src="camp.thumbnail"
                        class="row-media"
                        muted
                        loop
                        onmouseover="this.play()"
                        onmouseout="this.pause()"
                      ></video>
                      <img
                        v-else
                        :src="camp.thumbnail"
                        class="row-media"
                        alt="Thumbnail"
                      />
                    </template>
                    <template v-else>
                      <i
                        v-if="camp.format === 'screensaver'"
                        class="bi bi-aspect-ratio"
                      ></i>
                      <i v-else class="bi bi-view-list"></i>
                    </template>
                  </div>
                  {{ camp.name }}
                </div>
                <div class="col-format">{{ camp.format }}</div>
                <div class="col-status">
                  <span
                    class="status-badge"
                    :class="getStatusColor(camp.status)"
                    >{{ camp.status }}</span
                  >
                </div>
                <div class="col-duration">
                  {{ camp.itemCount }} items ({{ camp.totalDuration }}s)
                </div>
                <div class="col-date">{{ camp.lastModified }}</div>
                <div class="col-actions">
                  <button class="action-btn small" @click="action('edit_campaign', camp)" title="Edit">
                    <i class="bi bi-pencil-fill"></i>
                  </button>
                  <button
                    class="action-btn small"
                    title="Duplicate"
                    :disabled="duplicatingId === camp.id"
                    @click="duplicateCampaign(camp)"
                  >
                    <i
                      :class="duplicatingId === camp.id ? 'bi bi-hourglass-split' : 'bi bi-copy'"
                    ></i>
                  </button>
                  <button class="action-btn small delete" @click="action('delete_campaign', camp)" title="Delete">
                    <i class="bi bi-trash-fill"></i>
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div class="empty-icon"><i class="bi bi-collection-play"></i></div>
            <h3 class="empty-title">
              {{ campaigns.length === 0 ? "No campaigns yet" : "No matches found" }}
            </h3>
            <p class="empty-subtitle">
              {{
                campaigns.length === 0
                  ? "Create your first campaign to start scheduling screen content."
                  : "Try a different search term or status filter."
              }}
            </p>
            <button
              v-if="campaigns.length === 0"
              class="empty-cta"
              @click="navigateToCreateParams"
            >
              <i class="bi bi-plus-lg"></i> New Campaign
            </button>
          </div>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<style lang="scss" scoped>
.library-container {
  color: var(--text-primary);
  margin: 0 auto;
}
/* Header styles removed, now in PageHeader component */

/* Toolbar */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: white;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid var(--neutral-200);
}

.left-controls {
  display: flex;
  gap: 1rem;
  flex: 1;
}

.search-box {
  position: relative;
  width: 300px;

  i {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--neutral-400);
  }

  input {
    width: 100%;
    padding: 0.625rem 1rem 0.625rem 2.5rem;
    border: 1px solid var(--neutral-300);
    border-radius: 6px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(47, 77, 132, 0.1);
    }
  }
}

.filter-box select {
  padding: 0.625rem 2rem 0.625rem 1rem;
  border: 1px solid var(--neutral-300);
  border-radius: 6px;
  background-color: white;
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
}

.view-controls {
  display: flex;
  background: var(--neutral-100);
  padding: 0.25rem;
  border-radius: 8px;
  gap: 0.25rem;
}

.view-btn {
  border: none;
  background: none;
  padding: 0.5rem;
  border-radius: 6px;
  color: var(--neutral-500);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--neutral-900);
  }

  &.active {
    background: white;
    color: var(--primary-color);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

/* Grid View */
.grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.ad-card {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-color: var(--neutral-300);
  }
}

.card-preview {
  height: 160px;
  background: var(--neutral-100);
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &.has-thumbnail .preview-placeholder {
    display: none;
  }
}

.preview-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.preview-placeholder i {
  font-size: 3rem;
  color: var(--neutral-300);
}

.duration-badge {
  position: absolute;
  bottom: 0.75rem;
  right: 0.75rem;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.card-details {
  padding: 1rem;
}

.card-header {
  padding: 10px !important;
  display: flex;
  justify-content: space-between;
  align-items: center; /* Center align title and badge */
  margin-bottom: 0.5rem;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--neutral-900);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* status-dot removed */

.format-text {
  font-size: 0.875rem;
  color: var(--neutral-500);
  margin: 0 0 1rem;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: var(--neutral-400);
}

.status-text {
  font-weight: 500;

  &.status-active {
    color: #10b981;
  }
  &.status-inactive {
    color: var(--neutral-500);
  } // Inactive text usually gray
  &.status-scheduled {
    color: #d97706;
  }
  &.status-draft {
    color: #4f46e5;
  }
}

/* Card Actions Overlay for Grid */
.card-actions {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0 !important;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--neutral-200);
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
  color: var(--primary-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease,
    border-color 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;

  i {
    font-size: 13px;
    line-height: 1;
  }

  &:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: var(--primary-hover);
    box-shadow: 0 2px 6px rgba(47, 77, 132, 0.18);
  }

  &:active {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    pointer-events: none;
  }

  &.delete {
    color: #ef4444;

    &:hover {
      background: #fee2e2;
      border-color: #fecaca;
      color: #dc2626;
      box-shadow: 0 2px 6px rgba(239, 68, 68, 0.2);
    }
  }
}

/* List View */
.list-view {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  overflow: hidden;
}

.list-header {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr 1fr 100px;
  padding: 1rem 1.5rem;
  background: var(--neutral-50);
  border-bottom: 1px solid var(--neutral-200);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.list-row {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr 1fr 100px;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--neutral-100);
  align-items: center;
  transition: background-color 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--neutral-50);
  }
}

.row-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  background: var(--neutral-100);
  border-radius: 6px;
  margin-right: 0.75rem;
  color: var(--neutral-500);
  overflow: hidden;
}

.row-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.col-name {
  font-weight: 500;
  color: var(--neutral-900);
  display: flex;
  align-items: center;
}

.col-format,
.col-duration,
.col-date {
  font-size: 0.875rem;
  color: var(--neutral-500);
}

.status-badge {
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;

  &.status-active {
    background: #dcfce6;
    color: #166434;
  }
  &.status-inactive {
    background: var(--neutral-100);
    color: var(--neutral-500);
  }
  &.status-scheduled {
    background: #fef9c3;
    color: #844d0f;
  }
  &.status-draft {
    background: #e0e7ff;
    color: #4f46e5;
  }
}

.col-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.action-btn.small {
  width: 28px;
  height: 28px;
  font-size: 0.75rem;
  box-shadow: none;
  background: transparent;
  border-color: transparent;
  color: var(--primary-color);

  &:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: var(--primary-hover);
    box-shadow: none;
  }

  &.delete {
    color: #ef4444;

    &:hover {
      background: #fee2e2;
      border-color: #fecaca;
      color: #dc2626;
      box-shadow: none;
    }
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: var(--neutral-400);

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--neutral-300);
  }
}
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] {
  .library-container {
    color: #e8edf5;
  }

  .toolbar {
    background: #161c2c;
    border-color: #2d3f5a;
  }

  .search-box {
    i { color: #6480a0; }

    input {
      background: #28364e;
      border-color: #3d5070;
      color: #e8edf5;

      &::placeholder { color: #6480a0; }

      &:focus {
        border-color: #4e7fff;
        box-shadow: 0 0 0 2px rgba(78, 127, 255, 0.2);
      }
    }
  }

  .filter-box select {
    background-color: #28364e;
    border-color: #3d5070;
    color: #e8edf5;

    &:focus { border-color: #4e7fff; }
  }

  .view-controls {
    background: #161c2c;
  }

  .view-btn {
    color: #9aadc4;

    &:hover { color: #e8edf5; }

    &.active {
      background: #3d5070;
      color: #4e7fff;
    }
  }

  /* Grid cards */
  .ad-card {
    background: #28364e;
    border-color: #3d5070;

    &:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5);
      border-color: #4e6a8a;
    }
  }

  .card-preview {
    background: #1e2a3e;
  }

  .preview-placeholder i {
    color: #3d5070;
  }

  .card-header h3 {
    color: #e8edf5;
  }

  .format-text {
    color: #9aadc4;
  }

  .card-meta {
    color: #6480a0;
  }

  .action-btn {
    background: #1e2a3e;
    border-color: #3d5070;
    color: #6b96ff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);

    &:hover {
      background: #1a2d52;
      border-color: #4e7fff;
      color: #a8c4ff;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.55);
    }

    &.delete {
      color: #f87171;

      &:hover {
        background: rgba(248, 113, 113, 0.18);
        border-color: rgba(248, 113, 113, 0.5);
        color: #fca5a5;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.55);
      }
    }
  }

  /* List view */
  .list-view {
    background: #28364e;
    border-color: #3d5070;
  }

  .list-header {
    background: #1e2a3e;
    border-bottom-color: #2d3f5a;
    color: #6480a0;
  }

  .list-row {
    border-bottom-color: #2d3f5a;

    &:hover { background-color: #2a3f5e; }
  }

  .row-icon {
    background: #1e2a3e;
    color: #9aadc4;
  }

  .col-name {
    color: #e8edf5;
  }

  .col-format,
  .col-duration,
  .col-date {
    color: #9aadc4;
  }

  /* Status badges — adjusted for dark backgrounds */
  .status-badge {
    &.status-active {
      background: rgba(16, 185, 129, 0.15);
      color: #34d399;
    }
    &.status-inactive {
      background: rgba(107, 114, 128, 0.2);
      color: #9aadc4;
    }
    &.status-scheduled {
      background: rgba(217, 119, 6, 0.15);
      color: #fbbf24;
    }
    &.status-draft {
      background: rgba(99, 102, 241, 0.15);
      color: #818cf8;
    }
  }

  .action-btn.small {
    background: transparent;
    border-color: transparent;
    color: #6b96ff;
    box-shadow: none;

    &:hover {
      background: rgba(78, 127, 255, 0.18);
      border-color: rgba(78, 127, 255, 0.45);
      color: #a8c4ff;
      box-shadow: none;
    }

    &.delete {
      color: #f87171;

      &:hover {
        background: rgba(248, 113, 113, 0.18);
        border-color: rgba(248, 113, 113, 0.5);
        color: #fca5a5;
        box-shadow: none;
      }
    }
  }

  /* Empty state */
  .empty-state {
    color: #6480a0;

    i { color: #3d5070; }
  }
}

/* ============================================================
   ── Enhancement layer — refined campaign gallery ───────────
   Layered after the base rules; wins by source order.
   ============================================================ */

/* Status filter → segmented pill control */
.status-pills {
  display: inline-flex;
  background: var(--neutral-100);
  border: 1px solid var(--neutral-200);
  border-radius: 9px;
  padding: 3px;
  gap: 2px;
}
.status-pill {
  border: none;
  background: none;
  padding: 7px 16px;
  border-radius: 7px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--neutral-500);
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.status-pill:hover { color: var(--neutral-900); }
.status-pill.active {
  background: #fff;
  color: var(--primary-color, #2f4476);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
}

/* Preview: clip + zoom + reveal overlay */
.card-preview { overflow: hidden; }
.preview-media { transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1); }
.ad-card:hover .preview-media { transform: scale(1.05); }
.card-preview.has-thumbnail::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(15, 23, 42, 0.32), transparent 55%);
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
}
.ad-card:hover .card-preview.has-thumbnail::after { opacity: 1; }

/* Format chip */
.format-chip {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.6);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
  text-transform: capitalize;
}
.format-chip i { font-size: 0.72rem; }

/* Video play affordance */
.play-affordance {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  z-index: 1;
  pointer-events: none;
}
.play-affordance i {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  font-size: 24px;
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background 0.2s ease;
}
.ad-card:hover .play-affordance i {
  transform: scale(1.08);
  background: var(--primary-color, #2f4476);
}

/* duration badge above the overlay */
.duration-badge {
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* Tidy header / detail rhythm */
.card-header {
  padding: 16px 16px 10px !important;
  margin-bottom: 0;
  gap: 10px;
  border-bottom: none; /* drop the inherited global .card-header divider */
}
.card-header h3 {
  min-width: 0; /* allow ellipsis truncation inside the flex row */
  flex: 1;
}
.card-header .status-badge {
  flex-shrink: 0; /* badge keeps its size; never wraps or squishes */
  white-space: nowrap;
}
.card-details { padding: 0 16px 16px; }
.format-text { margin: 0 0 0.625rem; }

/* Staggered reveal */
.grid-view .ad-card {
  animation: camp-card-in 0.42s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  animation-delay: calc(var(--d, 0) * 28ms);
}
@keyframes camp-card-in {
  from { opacity: 0; transform: translateY(10px); }
}

/* Shimmer skeleton (replaces the invisible .loader) */
.skeleton-grid { pointer-events: none; }
.skeleton-card {
  background: #fff;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  overflow: hidden;
}
.skeleton-thumb { height: 160px; }
.skeleton-body { padding: 1rem; display: flex; flex-direction: column; gap: 10px; }
.skeleton-thumb,
.skeleton-line {
  background: linear-gradient(90deg, #eef2f7 25%, var(--neutral-200) 50%, #eef2f7 75%);
  background-size: 200% 100%;
  animation: camp-shimmer 1.4s infinite;
}
.skeleton-line { height: 12px; border-radius: 6px; }
.skeleton-line.short { width: 55%; }
@keyframes camp-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Refined empty state */
.empty-state .empty-icon {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  margin-bottom: 18px;
  background: var(--neutral-100);
  color: var(--neutral-400);
}
.empty-state .empty-icon i { font-size: 32px; margin: 0; color: inherit; }
.empty-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 6px;
}
.empty-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 20px;
  max-width: 360px;
  text-align: center;
}
.empty-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border: none;
  border-radius: 9px;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  background: var(--primary-color, #2f4476);
  transition: background 0.2s ease, transform 0.15s ease;
}
.empty-cta:hover { background: var(--primary-hover, #253864); transform: translateY(-1px); }

@media (prefers-reduced-motion: reduce) {
  .grid-view .ad-card { animation: none; }
  .preview-media,
  .skeleton-thumb,
  .skeleton-line { transition: none; animation: none; }
}

/* ── Dark mode — enhancement layer ── */
[data-theme="dark"] {
  .status-pills { background: #161c2c; border-color: #2d3f5a; }
  .status-pill { color: #9aadc4; }
  .status-pill:hover { color: #e8edf5; }
  .status-pill.active { background: #3d5070; color: #cdd9ea; box-shadow: none; }

  .ad-card:hover .play-affordance i { background: #4e7fff; }

  .skeleton-card { background: #28364e; border-color: #3d5070; }
  .skeleton-thumb,
  .skeleton-line {
    background: linear-gradient(90deg, #1e2a3e 25%, #2b3a54 50%, #1e2a3e 75%);
    background-size: 200% 100%;
  }

  .empty-state .empty-icon { background: #1e2a3e; color: #6480a0; }
  .empty-title { color: #e8edf5; }
  .empty-subtitle { color: #9aadc4; }
  .empty-cta { background: #3a5cc0; }
  .empty-cta:hover { background: #4e7fff; }
}
</style>
